import React, { useMemo, useRef } from 'react'
import {
  TooltipPositions,
  TooltipOverlayType,
  Tooltip,
} from 'src/components/Reusable/Tooltip/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Popover from 'react-bootstrap/Popover'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { AUDIT_RESULT } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { NoPaddingGridColumn } from 'src/components/Reusable/Grid/Grid.styles'
import { TitleText } from 'src/components/Reusable/Text/Title'
import {
  ControlPolicyInfo,
  PolicyInfo,
} from 'src/components/Reusable/ListItem/ListItem.components'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useSetControlStatus } from 'src/components/async/model/control/useControlMutations'
import isFunction from 'lodash/isFunction'
import { LIST_ITEM_COPY } from 'src/components/Reusable/ListItem/ListItem.constants'
import { Pill } from 'src/components/Reusable/Pill'
import palette from 'src/designSystem/variables/palette'
import { ControlStatusDropdown } from 'src/components/PageLayout/ShowPage/Dropdowns/ControlStatusDropdown'
import { useAuditScanProgress } from 'src/pages/Roadmap_v2/smartchecks/context/AuditScanProgressContext'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { Spinner } from 'src/Throbber'
import { ContentMatchBadge } from 'src/components/Reusable/Search/ContentSearch/ContentSearch.components'
import { MarkControlTestCurrentButton } from 'src/pages/AuditBot/MarkControlTestsCurrent'
import {
  TitleContainer,
  CloseIcon,
  Divider,
  MenuContainer,
} from 'src/components/Reusable/FloatingMenu/FloatingMenu.styles'
import { useBasicDropdown } from 'src/components/Reusable/BasicDropdown/context/BasicDropdownContext'
import { useLabelMap } from 'src/context/LabelsContext'
import { AssigneeButton } from 'src/components/Reusable/Buttons/AssigneeButton'
import { ActionGridColumn, GridColumn } from '../../../components/Reusable/Grid'
import { BasicChip } from '../../../components/Reusable/Chips/BasicChip'
import { DepartmentChip, DepartmentSummaryBadge } from '../DepartmentChip'
import { ModelComplianceFrameWorkInfo } from '../../../components/Reusable/Text/ModelComplianceFrameworkInfo'
import { useComplianceFrameworks } from '../../../components/async/complianceframework/useComplianceFrameworks'
import { MODEL_TYPES } from '../../../Utils/globalEnums'
import { StyledModelId } from '../ControlsShowPage/ControlsShowPage.styles'
import { useControlChecksContext } from './ControlChecksContext'
import { OwnerAssignmentButton } from './OwnerAssignmentButton'
import { DescriptionMarkup } from './DescriptionMarkup'
import { ControlsListItemMenu } from './ControlsListItemMenu/ControlsListItemMenu'
import {
  ControlCheckResultPillsContainer,
  PolicyGridColumn,
  LabelTooltipContainer,
  StyledLabelCount,
} from './ControlsListItem.styles'
import {
  getComplianceFrameworksForControl,
  getControlCheckResultPillColor,
  getControlCheckResultPillLabel,
  getControlsListItemConfig,
  getMismatch,
} from './ControlsListItem.helpers'
import {
  ControlListItemConfig,
  ControlMutator,
} from './ControlListItem.constants'

const MAX_MODEL_ID_LENGTH = 20

export const ControlModelId = ({
  modelId,
}: {
  modelId: string
}): JSX.Element => {
  return modelId.length > MAX_MODEL_ID_LENGTH ? (
    <Tooltip
      id={`controls-index-row-modelid-tooltip-${modelId}`}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={modelId}
    >
      <StyledModelId>{modelId}</StyledModelId>
    </Tooltip>
  ) : (
    <>{modelId}</>
  )
}

export const ControlChecksListItemNoTestResult = ({
  isNotApplicable,
  testName,
}: {
  isNotApplicable: boolean
  testName: CONTROL_TEST_NAMES
}): JSX.Element => {
  return isNotApplicable ? (
    <Pill
      label={getControlCheckResultPillLabel(AUDIT_RESULT.NOT_APPLICABLE)}
      backgroundColor={getControlCheckResultPillColor(
        AUDIT_RESULT.NOT_APPLICABLE,
      )}
      $fontColor={palette.neutral.white}
      $size="small"
    />
  ) : (
    <Pill
      label={getControlCheckResultPillLabel(AUDIT_RESULT.NOT_RUN, testName)}
      backgroundColor={getControlCheckResultPillColor(AUDIT_RESULT.NOT_RUN)}
      $fontColor={palette.neutral.white}
      $size="small"
    />
  )
}

export const ControlCheckResultPills = ({
  result,
  testName,
  isStale,
  controlTestId,
}: {
  result: AUDIT_RESULT
  testName: CONTROL_TEST_NAMES
  isStale: boolean
  controlTestId: string
}): JSX.Element => {
  return (
    <ControlCheckResultPillsContainer>
      <Pill
        label={getControlCheckResultPillLabel(result, testName)}
        backgroundColor={getControlCheckResultPillColor(result)}
        $fontColor={palette.neutral.white}
        $size="small"
      />
      {isStale && (
        <Pill
          label="Outdated"
          backgroundColor={palette.orange['700']}
          $fontColor={palette.neutral.white}
          $size="small"
        />
      )}
      {/* Readiness returns only the latest result per control, so no latest check
          is needed here — unlike the Control Test tab, which lists every run. */}
      <MarkControlTestCurrentButton
        controlTestId={controlTestId}
        isStale={isStale}
      />
    </ControlCheckResultPillsContainer>
  )
}

export const ControlsListItemMenuColumn = ({
  control,
  config,
}: {
  control: Control.AsObject
  config?: ControlListItemConfig
}): JSX.Element => {
  const menuConfig = getControlsListItemConfig(config)
  return (
    <ActionGridColumn>
      {menuConfig?.customMenu ? (
        <menuConfig.customMenu control={control} />
      ) : (
        <ControlsListItemMenu control={control} />
      )}
    </ActionGridColumn>
  )
}

export const ControlsListItemOwnerColumn = ({
  control,
  mutate,
}: {
  control: Control.AsObject
  mutate: ControlMutator
}): JSX.Element => (
  <GridColumn>
    <OwnerAssignmentButton control={control} mutate={mutate} />
  </GridColumn>
)

export const ControlsListItemAssigneesColumn = ({
  control,
  onUpdate,
}: {
  control: Control.AsObject
  onUpdate?: () => void
}): JSX.Element => (
  <GridColumn justifyContent="flex-start">
    <AssigneeButton
      modelId={control.id}
      modelType={MODEL_TYPE.CONTROL}
      assigneeEmails={control.assigneeEmailsList ?? []}
      ownerEmail={control.ownerEmail || null}
      onUpdate={onUpdate}
    />
  </GridColumn>
)

export const ControlsListItemStatusColumn = ({
  control,
  mutate,
}: {
  control: Control.AsObject
  mutate: ControlMutator
}): JSX.Element => {
  const setControlStatus = useSetControlStatus()

  const updateStatus = async (
    status: CONTROL_STATUS,
    customStatusId?: string,
  ) => {
    await setControlStatus({
      controlId: control.id,
      status,
      customStatusId,
    })
    if (isFunction(mutate)) {
      await mutate()
    }
  }

  return (
    <GridColumn>
      <ControlStatusDropdown
        control={control}
        initialStatus={control.status as CONTROL_STATUS}
        onSelect={updateStatus}
      />
    </GridColumn>
  )
}

export const RiskSummaryBadge = ({
  risks,
  subjectId,
}: {
  risks: { id: string; customId: string; name: string }[]
  subjectId: string
}): JSX.Element => {
  const formattedRisks = useMemo(
    () => risks.map((r) => `${r.customId} ${r.name}`.trim()).filter(Boolean),
    [risks],
  )

  if (formattedRisks.length === 0) {
    return <BasicChip>-</BasicChip>
  }

  const [primaryRisk, ...remainingRisks] = formattedRisks
  const hasMore = remainingRisks.length > 0

  return hasMore ? (
    <Tooltip
      id={`risk-summary-tooltip-${subjectId}`}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={remainingRisks.join(', ')}
    >
      <div>
        <BasicChip>{`${primaryRisk} + ${remainingRisks.length} more`}</BasicChip>
      </div>
    </Tooltip>
  ) : (
    <BasicChip>{primaryRisk}</BasicChip>
  )
}

export const ControlsListItemRisksColumn = ({
  control,
}: {
  control: Control.AsObject
}): JSX.Element => (
  <GridColumn>
    <RiskSummaryBadge
      risks={control.linkedRisksList || []}
      subjectId={control.id}
    />
  </GridColumn>
)

export const ControlsListItemEvidenceColumn = ({
  show,
  control,
}: {
  show: boolean
  control: Control.AsObject
}): JSX.Element => {
  return <GridColumn>{show && <>{control.linkedEvidenceCount}</>}</GridColumn>
}

export const ControlsListItemDepartmentColumn = ({
  show,
  controlId,
  departmentId,
  departmentNames,
}: {
  show: boolean
  controlId: string
  departmentId: string
  departmentNames?: string[]
}): JSX.Element => (
  <GridColumn>
    {show &&
      (departmentNames && departmentNames.length > 0 ? (
        <DepartmentSummaryBadge
          departmentNames={departmentNames}
          subjectId={controlId}
        />
      ) : (
        <DepartmentChip departmentId={departmentId} />
      ))}
  </GridColumn>
)

export const ControlsListItemContentMatchColumn = ({
  isContentMatch,
}: {
  isContentMatch: boolean
}): JSX.Element => (
  <GridColumn>
    <ContentMatchBadge isContentMatch={isContentMatch} />
  </GridColumn>
)

export const ControlsListItemModelIdColumn = ({
  modelId,
}: {
  modelId: string
}): JSX.Element => (
  <GridColumn>
    <ControlModelId modelId={modelId} />
  </GridColumn>
)

export const ControlsListItemControlNameColumn = ({
  control,
  isControlNa,
  policyNames,
}: {
  control: Control.AsObject
  isControlNa: boolean
  policyNames?: string[]
}): JSX.Element => (
  <PolicyGridColumn>
    <Tooltip
      id={`controls-index-row-tooltip-${control.name}`}
      placement={TooltipPositions.right}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={<DescriptionMarkup objective={control.objective} />}
    >
      <TitleText>{control.name}</TitleText>
    </Tooltip>
    {policyNames && policyNames.length > 1 ? (
      <Tooltip
        id={`controls-index-row-policies-tooltip-${control.id}`}
        placement={TooltipPositions.top}
        overlayType={TooltipOverlayType.popover}
        tooltipBody={policyNames.join(', ')}
      >
        <div style={{ width: '100%', minWidth: 0 }}>
          <PolicyInfo policyName={policyNames.join(', ')} isWarning={false} />
        </div>
      </Tooltip>
    ) : (
      <ControlPolicyInfo
        control={{
          ...control,
          policyName:
            policyNames && policyNames.length > 0
              ? policyNames.join(', ')
              : control.policyName,
        }}
        isControlNa={isControlNa}
      />
    )}
  </PolicyGridColumn>
)

export const ControlsListItemFrameworksColumn = ({
  audit,
  complianceFrameworkIds,
}: {
  audit?: AuditRecord
  complianceFrameworkIds: string[]
}): JSX.Element => {
  const { data: cfData, isLoading: isCfLoading } = useComplianceFrameworks(true)

  if (!cfData || isCfLoading) {
    return (
      <GridColumn>
        <></>
      </GridColumn>
    )
  }

  const complianceFrameworks = cfData.getItemsList() || []

  const complianceFrameworksForControl = getComplianceFrameworksForControl(
    complianceFrameworks,
    complianceFrameworkIds,
  )
  const complianceFrameworksInUse = audit
    ? audit.getComplianceFrameworksList()
    : complianceFrameworks
  const mismatch = getMismatch(
    complianceFrameworksForControl.map((el) => el.getModelId()),
    complianceFrameworksInUse,
    !!audit,
  )
  const frameworkNames = complianceFrameworksForControl
    .map((framework) => framework.getName())
    .join(', ')
  const hasComplianceFrameworks = complianceFrameworksForControl.length > 0
  const cfNames = hasComplianceFrameworks
    ? frameworkNames
    : LIST_ITEM_COPY.NO_FRAMEWORK

  return (
    <GridColumn>
      <ModelComplianceFrameWorkInfo
        modelComplianceFrameworkNames={cfNames}
        isWarning={!hasComplianceFrameworks}
        mismatch={mismatch}
        modelType={MODEL_TYPES.CONTROL}
      />
    </GridColumn>
  )
}

// A test drops out of the scan map the moment it finishes, but the readiness refetch it
// triggers lands a beat later. Keep reporting it as running across that gap so the row
// holds its spinner instead of flashing the previous result.
const useRunningUntilRefreshed = (
  isRunning: boolean,
  isRefreshing: boolean,
): boolean => {
  const wasRunning = useRef(isRunning)
  const awaitingResult = useRef(false)

  if (wasRunning.current !== isRunning) {
    wasRunning.current = isRunning
    if (!isRunning) awaitingResult.current = true
  }
  if (awaitingResult.current && !isRefreshing) {
    awaitingResult.current = false
  }

  return isRunning || awaitingResult.current
}

export const ControlsListItemAIControlCheckColumns = ({
  controlId,
  isNotApplicable,
  migratedControlId,
  showOE,
}: {
  controlId: string
  isNotApplicable: boolean
  migratedControlId?: string
  showOE: boolean
}): JSX.Element => {
  // readiness is fetched once for the account by ControlChecksProvider
  const { getTests, isLoading, isRefreshing } = useControlChecksContext()
  const tests = getTests(controlId, migratedControlId)
  const { getAuditRunId } = useAuditScanProgress()
  const isScanTesting = useMemo(
    () => !!getAuditRunId(migratedControlId || controlId),
    [migratedControlId, controlId, getAuditRunId],
  )
  const isTesting = useRunningUntilRefreshed(isScanTesting, isRefreshing)

  if (isLoading) {
    return (
      <>
        {showOE && (
          <NoPaddingGridColumn>
            <span>-</span>
          </NoPaddingGridColumn>
        )}
      </>
    )
  }

  // a control scanned for the first time has no readiness record yet, so let the spinner
  // below win rather than briefly claiming it has no result
  if (isNotApplicable || (!tests && !isTesting)) {
    return (
      <>
        {showOE && (
          <NoPaddingGridColumn>
            <ControlCheckResultPillsContainer>
              <ControlChecksListItemNoTestResult
                isNotApplicable={isNotApplicable}
                testName={CONTROL_TEST_NAMES.SPOT_CHECK}
              />
            </ControlCheckResultPillsContainer>
          </NoPaddingGridColumn>
        )}
      </>
    )
  }

  const spotCheck = tests?.find(
    (test) => test.getTestName() === CONTROL_TEST_NAMES.SPOT_CHECK,
  )

  return (
    <>
      {showOE && (
        <NoPaddingGridColumn>
          {isTesting ? (
            <FlexRow gap={8} align={FlexAlign.CENTER}>
              <Spinner color="primary" size="s" />
              <span>Testing</span>
            </FlexRow>
          ) : spotCheck ? (
            <ControlCheckResultPills
              result={spotCheck.getResult()}
              testName={CONTROL_TEST_NAMES.SPOT_CHECK}
              isStale={!!spotCheck.getIsStale()}
              controlTestId={spotCheck.getId()}
            />
          ) : (
            <ControlCheckResultPillsContainer>
              <ControlChecksListItemNoTestResult
                isNotApplicable={isNotApplicable}
                testName={CONTROL_TEST_NAMES.SPOT_CHECK}
              />
            </ControlCheckResultPillsContainer>
          )}
        </NoPaddingGridColumn>
      )}
    </>
  )
}

export const LabelCountColumn = ({
  control,
}: {
  control: Control.AsObject
}): JSX.Element => {
  const { openDropdownId, setOpenDropdownId } = useBasicDropdown()
  const labelIds = control.labelIdsList
  const popoverId = `label-popover-${control.id}`
  const isOpen = openDropdownId === popoverId

  const handleToggle = (show: boolean) => {
    if (show) {
      setOpenDropdownId(popoverId)
    } else if (isOpen) {
      setOpenDropdownId(null)
    }
  }

  if (!labelIds || labelIds.length === 0) {
    return <PolicyGridColumn>-</PolicyGridColumn>
  }

  return (
    <PolicyGridColumn>
      <OverlayTrigger
        show={isOpen}
        onToggle={handleToggle}
        trigger="click"
        placement="right-start"
        rootClose
        overlay={
          <Popover
            id={popoverId}
            style={{
              background: 'transparent',
              border: 'none',
              boxShadow: 'none',
              zIndex: 1060,
            }}
          >
            <MenuContainer
              isOpen={true}
              style={{
                position: 'relative',
                opacity: 1,
                visibility: 'visible',
              }}
            >
              <LabelDisplay
                labels={labelIds}
                onClose={() => handleToggle(false)}
              />
            </MenuContainer>
          </Popover>
        }
      >
        <StyledLabelCount
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {labelIds.length}
        </StyledLabelCount>
      </OverlayTrigger>
    </PolicyGridColumn>
  )
}

export const LabelDisplay = ({
  labels,
  onClose,
}: {
  labels: string[]
  onClose: () => void
}): JSX.Element => {
  const labelMap = useLabelMap()

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      <TitleContainer>
        <span>Labels</span>
        <CloseIcon onClick={onClose} />
      </TitleContainer>
      <Divider />
      <LabelTooltipContainer>
        {labels.map((label) => (
          <Pill
            key={label}
            label={labelMap[label] || label}
            useBorder={true}
            $size="small"
          />
        ))}
      </LabelTooltipContainer>
    </div>
  )
}
